import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaFileInvoiceDollar, // For RTI
  FaTruckPickup, // For Roadside Assistance
  FaOilCan, // For Consumables Cover
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
  FaCarSide,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const ToyotaCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is zero depreciation useful for Fortuner or Innova Crysta?",
      answer:
        "Yes, it reduces your out-of-pocket repair costs, especially in early years.",
    },
    {
      question: "Are Toyota hybrid models more expensive to insure?",
      answer:
        "Slightly yes, due to higher value of hybrid components and advanced systems.",
    },
    {
      question: "Can I get insurance for Toyota Urban Cruiser Hyryder?",
      answer:
        "Yes. Many insurers now offer hybrid-specific policies with suitable add-ons.",
    },
    {
      question: "Which Toyota model is cheapest to insure?",
      answer:
        "Yaris and Glanza typically offer lower premiums due to lower IDV and simple parts.",
    },
    {
      question: "Can expired Toyota policies be renewed online?",
      answer: "Yes, though older lapses may require inspection before renewal.",
    },
    {
      question: "Do diesel variants cost more to insure than petrol?",
      answer:
        "Yes, diesel variants generally attract slightly higher premiums.",
    },
    {
      question: "What happens to insurance if I sell my Toyota?",
      answer:
        "You can transfer the policy to a buyer or cancel and claim refund on unused period.",
    },
    {
      question: "Are accessories like alloy wheels covered if added later?",
      answer:
        "Only if declared and added in the policy. Undeclared accessories may cause claim rejection.",
    },
    {
      question:
        "Does comprehensive Toyota insurance include flood and fire cover?",
      answer:
        "Yes, as part of a comprehensive plan, it covers natural calamities and fire.",
    },
    {
      question: "Is No Claim Bonus transferable when switching Toyota cars?",
      answer:
        "Yes, NCB can be retained if the vehicle is registered in your name.",
    },
    {
      question: "Are tyres covered in basic Toyota policies?",
      answer:
        "Only for accident-related damages. Use tyre cover for extended protection.",
    },
    {
      question: "Can I get cashless claims at Toyota authorized centers?",
      answer:
        "Yes, if the insurer has tie-ups with service centres in their network.",
    },
    {
      question: "Can I add multiple drivers to Toyota policy?",
      answer:
        "Yes. Insurers allow you to declare named drivers under the same policy.",
    },
    {
      question: "How long does Toyota claim processing usually take?",
      answer:
        "Typically seven to fourteen working days depending on documentation and damage assessment.",
    },
    {
      question: "Does car colour affect Toyota insurance cost?",
      answer: "No, colour choices do not influence insurance premiums.",
    },
    {
      question: "Can I switch insurers mid-policy?",
      answer:
        "Not generally. Switching is usually possible only at renewal time.",
    },
    {
      question: "Is engine protection add-on necessary for Toyota hybrids?",
      answer:
        "Yes, it provides better protection for electronic and hybrid components.",
    },
    {
      question: "Can I insure a 10-year-old Toyota car?",
      answer:
        "Yes, third-party and some comprehensive plans are available depending on eligibility.",
    },
    {
      question: "What is the penalty for driving a Toyota without insurance?",
      answer:
        "Fines range from ₹2,000 to ₹4,000 under Indian Motor Law with possible licence suspension.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description:
        "Helps avoid deductions on plastic, fibre or composite parts.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine and Hybrid Component Protection",
      description:
        "Important if you drive in flood-prone areas or own hybrid variants.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Return to Invoice (RTI)",
      description:
        "Useful for expensive models like Fortuner or Innova Hycross.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Helps during long trips, flat tyres or breakdowns.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description:
        "Pays for small costs like oils, brake fluids or nuts used during repairs.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];
const toyotainsurance = [
    {
      name: 'Innova Crysta',
      path: '/toyota-innova-crysta-car-insurance',
      image: '/assets/toyotainnovacrysta.jpeg',
    },
      {
      name: 'Innova Hycross',
      path: '/toyota-innova-hycross-car-insurance',
      image: '/assets/toyotainnovahycross.jpeg',
    },
     {
      name: 'Fortuner',
      path: '/toyota-fortuner-car-insurance',
      image: '/assets/toyotafortuner.jpeg',
    },
      {
      name: 'Urban Cruiser Hyryder',
      path: '/toyota-urban-cruiser-hyryder-car-insurance',
      image: '/assets/toyotaurban.jpeg',
    },
    {
      name: 'Glanza',
      path: '/toyota-glanza-car-insurance',
      image: '/assets/toyotaglanza.jpeg',
    },
     {
      name: 'Hilux',
      path: '/toyota-hilux-car-insurance',
      image: '/assets/toyotahilux.jpeg',
    },
      {
      name: 'Camry',
      path: '/toyota-camry-car-insurance',
      image: '/assets/toyotacamry.jpeg',
    },
     {
      name: 'Vellfire',
      path: '/toyota-vellfire-car-insurance',
      image: '/assets/toyotavellfire.jpeg',
    },
  ];
  return (
    <>
      <Helmet>
        <title>
          Toyota Car Insurance in India – Compare Quotes & Buy Online
        </title>
        <meta
          name="description"
          content="Explore Toyota car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Toyota vehicle."
        />
        <meta
          name="keywords"
          content="toyota car insurance, auto insurance india, toyota fortuner insurance, toyota innova insurance, car insurance quotes"
        />
        <link
          rel="canonical"
          href="https://thefinmaster.com/toyota-car-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Toyota Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">
                    Toyota Car Insurance 
                  </span>{" "}
               
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Toyota is celebrated for its durability, performance, and long-lasting value on Indian roads. Whether you drive a practical Glanza or a feature-packed Fortuner, protecting your investment starts with the right insurance. This guide takes you through everything you need to know about insuring a Toyota vehicle, including average premiums, model-specific insights, useful add-ons, renewal tips, and frequently asked questions.

                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/toyatainsurance.jpeg"
                alt="Toyota Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Toyota in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Toyota Kirloskar Motor has maintained a strong position in the
            Indian car market by offering durable, fuel-efficient vehicles.
            Known for excellent after-sales service and trusted denim build,
            Toyota’s offerings include hatchbacks, sedans, SUVs and hybrid
            models.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Popular Toyota cars like the Innova Crysta, Fortuner and Urban
            Cruiser Hyryder are trusted by families and fleet operators alike.
            With Toyota ownership comes the expectation of dependability.
            Getting the right insurance combo is equally important.
          </p>
        </div>
      </div>


 <div className="p-4 sm:p-8 bg-white">
                              <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                                Choose Your Car Brand
                              </h2>
                              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                          {toyotainsurance.map((calc, index) => (
                            <motion.div
                              key={index}
                              whileHover={{ scale: 1.04 }}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.05 }}
                            >
                              <Link
                                to={calc.path}
                                className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                              >
                                <img
                                  src={calc.image}
                                  alt={calc.name}
                                  className="w-16 h-16 object-contain mb-2"
                                />
                                <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                  {calc.name}
                                </span>
                              </Link>
                            </motion.div>
                          ))}
                        </div>
                        </div>

      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Toyota Owners Need a Thoughtful Insurance Plan
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Toyota vehicles often include sophisticated safety systems, hybrid
            powertrains and premium interiors. Repair or replacement costs can
            be significant. A careful insurance plan helps protect against:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Accidents and collision repair</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Theft, vandalism or fire damage</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Events like floods, storms or natural disasters</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Third-party damage liabilities</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>
                High repair costs of hybrid components or premium interiors
              </span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            Your policy also gives access to Toyota-authorized service centers,
            cashless repair options and flexibility to add cover choices
            specific to your model.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Toyota Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here’s an overview of Toyota’s most popular cars in India, along with
          estimated IDV range and starting annual premiums for comprehensive
          policies:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "Yaris",
                category: "Sedan",
                idv: "6–9 Lakh",
                premium: "₹3,200 to ₹5,000",
              },
              {
                model: "Glanza",
                category: "Premium Hatchback",
                idv: "7–10 Lakh",
                premium: "₹3,500 to ₹5,600",
              },
              {
                model: "Urban Cruiser Hyryder",
                category: "Compact SUV Hybrid",
                idv: "11–14 Lakh",
                premium: "₹4,200 to ₹6,800",
              },
              {
                model: "Innova Crysta / Innova Hycross",
                category: "MUV / SUV Hybrid",
                idv: "16–22 Lakh",
                premium: "₹5,000 to ₹8,500",
              },
              {
                model: "Fortuner",
                category: "Full-Size SUV",
                idv: "23–32 Lakh",
                premium: "₹7,000 to ₹10,500",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Yaris", "Sedan", "6–9 Lakh", "₹3,200 to ₹5,000"],
                  [
                    "Glanza",
                    "Premium Hatchback",
                    "7–10 Lakh",
                    "₹3,500 to ₹5,600",
                  ],
                  [
                    "Urban Cruiser Hyryder",
                    "Compact SUV Hybrid",
                    "11–14 Lakh",
                    "₹4,200 to ₹6,800",
                  ],
                  [
                    "Innova Crysta / Innova Hycross",
                    "MUV / SUV Hybrid",
                    "16–22 Lakh",
                    "₹5,000 to ₹8,500",
                  ],
                  [
                    "Fortuner",
                    "Full-Size SUV",
                    "23–32 Lakh",
                    "₹7,000 to ₹10,500",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Add-Ons That Make Sense for Toyota Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Toyota cars often come equipped with advanced or hybrid technology. To
          further secure your investment, consider these useful add-ons:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Companies for Toyota Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          The following insurers are frequently selected by Toyota owners for
          their stable service, claim reliability and wide garage networks:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for digital policy management and strong reach.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Ideal for premium models like Innova and Fortuner.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Reliance General
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Offers good value pricing and quick issuance.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📋</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Flexible plans with helpful add-ons for hybrid vehicles.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              SBI General
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Budget-friendly and easy to buy for entry-level Toyota cars.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                How to Save on Toyota Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Consider these practical tips to reduce your premium while keeping
              coverage solid:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Compare multiple insurer quotes for the best option.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Maintain your No Claim Bonus (NCB) by avoiding minor claims.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Opt for a higher voluntary deductible if you drive safely.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Install certified anti-theft devices to earn premium
                  discounts.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Renew your policy on time to avoid inspection and penalties.
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Toyota Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To buy or renew your Toyota insurance policy, have these documents
              ready:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Vehicle Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance policy (if applicable)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>PAN or Aadhaar for verification</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>
                  Passport-size photos (only if doing offline submission)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Toyota Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Toyota cars are synonymous with quality, longevity and
                reliability. Choosing the right insurance plan ensures you stay
                protected on every drive. Whether you own a Glanza or a
                Fortuner, a tailored insurance policy can help you manage repair
                costs and streamline claims when needed.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you compare plans, assess the right
                add-ons and pick the smartest insurance options for your Toyota
                vehicle.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default ToyotaCarInsurance;
